import React from "react";
import { GridRowId, GridRowIdGetter, GridRowParams } from "@mui/x-data-grid-pro";
import { AavoDataGrid } from "src/components/common/dataGrid/AavoDataGrid.tsx";
import type { AavoDataGridProps } from "src/components/common/dataGrid/AavoDataGrid.tsx";
import { openFormForDataGridRow } from "src/components/common/dataGrid/dataGridFormUtils";
import {
	CrudActionButtonDefinitionWithShortcuts,
	removeActionShortcuts,
} from "src/components/common/crud/crudActionButtonDefinition.ts";
import { CrudActionButtons } from "src/components/common/crud/CrudActionButtons";
import { DataGridFormDefinition } from "src/components/common/dataGrid/types.tsx";
import { flattenAavoDataGridColumns } from "src/components/common/dataGrid/utils.ts";
import { GenericDialogContext } from "src/components/common/dialogs/GenericDialogContext.ts";
import { DataDirtyStateChangeHandler } from "src/utils/dataDirtyStateChangeHandler.ts";
import { useContextOrThrow } from "src/utils/useContextOrThrow.tsx";
import { useForwardedRef } from "src/utils/useForwardedRef.ts";
import { useMaybeControlledState } from "src/utils/useMaybeControlledState.ts";
import { isNotNullOrEmptyObject } from "src/utils/objectUtils.ts";
import i18n from "i18next";
import { useCrudGridRemoveAction } from "src/components/common/dataGrid/crud/useCrudGridRemoveAction.ts";

export interface FormCrudDataGridProps<RowData extends object>
	extends Omit<
		AavoDataGridProps<RowData>,
		| "getRowId"
		| "editMode"
		| "onCellEditStart"
		| "onRowEditStart"
		| "onRowModesModelChange"
		| "processRowUpdate"
		| "updateRows"
	> {
	refreshData?: () => Promise<unknown>;
	remove?: CrudActionButtonDefinitionWithShortcuts<
		{
			row: RowData;
			items: RowData[];
		},
		unknown
	>;
	form?: DataGridFormDefinition<RowData>;
	getRowId: GridRowIdGetter<RowData>;
	dataDirtyStateChanged?: DataDirtyStateChangeHandler;
}

export type FormCrudDataGridModelProps<RowData extends object> = Required<
	Pick<
		FormCrudDataGridProps<RowData>,
		| "rows"
		| "initialState"
		| "onStateChange"
		| "selectedRows"
		| "onRowSelectionChanged"
		| "getRowId"
		| "apiRef"
		| "gridId"
	>
> &
	Pick<FormCrudDataGridProps<RowData>, "refreshData" | "rowCount" | "actionBarMenuComponents2">;

export const FormCrudDataGrid = <RowData extends object>(props: FormCrudDataGridProps<RowData>) => {
	const {
		refreshData,
		getRowId,
		remove: removeProp,
		form,
		actionBarComponents,
		apiRef: forwardedApiRef,
		selectedRows: selectedRowsProp,
		onRowSelectionChanged: onRowSelectionChangedProp,
		onRowDoubleClick,
		columns,
		rows,
		...other
	} = props;
	const remove = removeActionShortcuts(removeProp);

	const { openDialog } = useContextOrThrow(GenericDialogContext);
	const gridApiRef = useForwardedRef(forwardedApiRef);

	const [selectedRowIds, setSelectedRowIds] = useMaybeControlledState<GridRowId[]>({
		controlledValue: selectedRowsProp,
		onChange: onRowSelectionChangedProp,
		defaultValue: [],
	});

	const getGridApi = React.useCallback(() => {
		const current = gridApiRef.current;
		if (isNotNullOrEmptyObject(current)) {
			return current;
		} else {
			const error = "GridApiRef has no value";
			console.error(error);
			throw new Error(error);
		}
	}, [gridApiRef]);

	const mappedColumns = flattenAavoDataGridColumns(columns).map((column) => ({
		...column,
		editable: false,
	}));
	const removeAction = useCrudGridRemoveAction({
		remove,
		selectedRowIds,
		getGridApi,
		getRows: () => rows,
		getRowId,
		onRowsRemoved: async () => {
			await refreshData?.();
		},
		clearSelection: () => setSelectedRowIds([]),
	});

	return (
		<AavoDataGrid
			apiRef={gridApiRef}
			columns={mappedColumns}
			getRowId={getRowId}
			refreshData={refreshData}
			actionBarComponents={
				<>
					<CrudActionButtons
						add={getAddAction()}
						save={{ type: "hidden" }}
						remove={removeAction}
						edit={getEditAction()}
					/>
					{actionBarComponents}
				</>
			}
			selectedRows={selectedRowIds}
			onRowSelectionChanged={setSelectedRowIds}
			onRowDoubleClick={async (params: GridRowParams<RowData>, event, details) => {
				if (onRowDoubleClick != null) {
					onRowDoubleClick(params, event, details);
				} else if (isEditEnabledForRow(params.row)) {
					await openFormForRow(params.row);
				}
			}}
			rows={rows}
			{...other}
		/>
	);

	function getAddAction() {
		if (form === undefined || !form.addRowEnabled) return { type: "hidden" as const };

		return {
			type: "enabled" as const,
			action: async () => {
				await openFormForRow(undefined);
			},
		};
	}

	function getEditAction() {
		if (form === undefined || !form.editEnabled) return { type: "hidden" as const };
		if (form.customEditAction != null)
			return form.customEditAction({
				openFormForRow: openFormForRow,
			});

		if (selectedRowIds.length !== 1) {
			return {
				type: "disabled" as const,
				tooltip: i18n.t("select_a_single_row"),
			};
		}

		const selectedEditableRow = getSelectedEditableRow();
		if (selectedEditableRow === "not-ready") return { type: "disabled" as const, tooltip: "" };
		if (selectedEditableRow === "no-selection") {
			return {
				type: "disabled" as const,
				tooltip: i18n.t("select_a_single_row"),
			};
		}

		if (selectedEditableRow.disabledReason !== undefined) {
			return {
				type: "disabled" as const,
				tooltip: selectedEditableRow.disabledReason,
			};
		}

		return {
			type: "enabled" as const,
			action: async () => {
				await openFormForRow(selectedEditableRow.row);
			},
		};
	}

	function getSelectedEditableRow(): { row: RowData; disabledReason?: string } | "no-selection" | "not-ready" {
		if (!isNotNullOrEmptyObject(gridApiRef.current)) return "not-ready";

		const firstRowId = selectedRowIds[0];
		const firstRow = firstRowId != null ? getGridApi().getRow<RowData>(firstRowId) : null;

		if (firstRow == null) return "no-selection";
		if (!isEditEnabledForRow(firstRow)) {
			const disabledReason =
				typeof form?.editEnabled === "function" ? form.editEnabled(firstRow) : undefined;
			return { row: firstRow, disabledReason: disabledReason ?? "" };
		}

		return { row: firstRow };
	}

	function isEditEnabledForRow(row: RowData): boolean {
		if (form === undefined) return false;
		if (typeof form.editEnabled === "function") return form.editEnabled(row) === undefined;
		return form.editEnabled;
	}

	async function openFormForRow(row: RowData | undefined) {
		if (form === undefined) return;

		await openFormForDataGridRow({
			row: row,
			form: form,
			getApi: getGridApi,
			openDialog: openDialog,
			refreshData: refreshData,
			keepOpenOnSave: form.keepOpenOnSave ?? false,
		});
	}
};
